import twitterText from "twitter-text";
import extension from "./extension.json";
import registry from "./registry.json";
export const publisherRelease = extension;
export function compareVersions(a: string, b: string) {
	if (!/^\d+\.\d+\.\d+$/.test(a)) return -1;
	const left = a.split(".").map(Number),
		right = b.split(".").map(Number);
	for (let i = 0; i < Math.max(left.length, right.length); i++) {
		const difference = (left[i] ?? 0) - (right[i] ?? 0);
		if (difference) return Math.sign(difference);
	}
	return 0;
}
export type PublishingMethod = "browser" | "api" | "manual";
export interface Field {
	format?: "plain" | "markdown" | "rich_text";
	key: string;
	label: [string, string];
	/** `image` is one picture, held as a brand file (`ondit-file:id`) until the task is saved. */
	kind: "text" | "textarea" | "select" | "url" | "image";
	/** What an `image` field takes, in the upload picker's `accept` form. */
	accept?: string;
	required: boolean;
	required_when?: { field: string; value: string };
	max_length: number | null;
	min_length?: number;
	length_metric?: "twitter";
	options?: string[];
	option_labels?: Record<string, [string, string]>;
	transfer: "text" | "guided";
	selectors: string[];
	/**
	 * What this platform puts in the box when it is empty.
	 *
	 * The assistant refuses to overwrite a field that already holds something,
	 * so that a draft somebody started is never replaced silently. Some editors
	 * make that refusal permanent: CSDN's title box opens holding 【无标题】,
	 * and Toutiao's body is a ProseMirror whose placeholder is a real
	 * `<p>请输入正文</p>` node rather than CSS, so both read as occupied and
	 * neither could ever be filled.
	 *
	 * Listing the strings here keeps the refusal — a real draft still stops the
	 * write — while letting the platform's own placeholder out of the way. It is
	 * data rather than a heuristic because only the platform knows its wording,
	 * and a guess that got it wrong would overwrite somebody's work.
	 */
	empty_values?: string[];
	/**
	 * Whether this editor keeps the words and throws the markup away.
	 *
	 * A rich write is checked by counting the links that survived it, because a
	 * body whose citation back to the ondit page has become plain text is not the
	 * body that was sent. Toutiao and Baijiahao flatten every anchor, every time,
	 * so without this the assistant would warn on every fill — and a warning that
	 * is always there is one nobody reads. Set it only for an editor observed to
	 * flatten, and say so in the platform's instructions, so the person knows the
	 * links are gone rather than wondering why nothing was said.
	 */
	flattens_links?: boolean;
}
export interface MediaLimits {
	mime_types?: string[];
	max_bytes?: number;
	min_width?: number;
	max_width?: number;
	min_height?: number;
	max_height?: number;
	min_duration_ms?: number;
	max_duration_ms?: number;
	aspect_ratios?: [number, number][];
}
export interface MediaRule {
	limits?: Partial<Record<"image" | "video", MediaLimits>>;
	accept: string;
	required: boolean;
	multiple: boolean;
	transfer: string;
	max_count?: number;
	min_count?: number;
	/** A video has to be the only item. */
	video_alone?: boolean;
	no_video?: boolean;
}
export interface PlatformContract {
	id: string;
	name: string;
	methods: PublishingMethod[];
	/** The credential provider an `api` publish goes out through, connected on the Integrations screen. */
	credential_provider?: string;
	/** How an account for it is connected, which is the Integrations screen the panel deep-links to. */
	credential_kind?: "oauth" | "service_account";
	entry_url: string;
	hosts: string[];
	dynamic_host: boolean;
	publication_hosts?: string[];
	publication_custom_domain?: boolean;
	preview: {
		layout: string;
		fields: Field[];
		media: MediaRule | null;
	};
	browser: { verification: string; fill: string; final_action: string; instructions?: [string, string] };
	/**
	 * What a browser with nobody watching needs to finish a post here, beyond
	 * the fields it fills. A contract without this is filled and handed back to
	 * a person, and a schedule refuses it: a send at 09:30 cannot ask.
	 *
	 * Every selector is one observed on the platform's own editor while signed
	 * in. None of it may be guessed — the control beside Post is Discard, and
	 * "it was not on the page" is not evidence when the page was still drawing.
	 */
	unattended?: Unattended;
	upstream_ref: string;
	/**
	 * The kinds of post the platform takes, each its own editor: Xiaohongshu's
	 * image note, video and long article open at three addresses and ask for
	 * three sets of fields. The first is the default and what the top level
	 * already describes; the others override it. A contract without them has
	 * one kind and no choice.
	 */
	formats?: PlatformFormat[];
	/** Set on a contract resolved by `withFormat`: which of `formats` it is. */
	format?: string;
}
export interface Unattended {
	/** URL patterns for editors whose route is not one of the hand-written adapters. */
	editor_paths?: string[];
	/** Links whose one distinct href says which account this is. Never an editor. */
	account: string;
	/** Some editors expose the signed-in author as one unique, visible control rather than a profile link. */
	account_text?: boolean;
	/** A same-origin, non-secret browser marker used where an editor exposes no profile control. */
	account_storage?: string;
	/** A stable attribute on the one visible signed-in account control; it is hashed before use. */
	account_attribute?: string;
	/** A capture from the editor URL that is scoped to the signed-in destination, such as a notebook id. */
	account_location?: string;
	/** A preliminary control that opens the final publish settings. */
	open_submit?: string;
	/** A reversible control that opens the editor before any task content is written. */
	open_editor?: string;
	/** Exact accessible labels for the editor-opening control. */
	open_editor_text?: string[];
	/** The control that sends. Clicked once; never found by the words on it. */
	submit: string;
	/** Exact accessible labels used only when the platform does not expose a stable final-action attribute. */
	submit_text?: string[];
	/** Exact accessible labels for the preliminary settings action. */
	open_submit_text?: string[];
	/** A confirmation control shown only after the first publish action. */
	confirm_submit?: string;
	/** Exact accessible labels for the confirmation control. */
	confirm_submit_text?: string[];
	/** Chat composers whose platform-defined final action is the Enter key. */
	submit_key?: "Enter";
	/** Where the new post's address appears afterwards. */
	result?: string;
	/** Set where the editor navigates to the post rather than showing its link. */
	lands_on_post?: boolean;
	/** Selectorless fields the adapter sets through a platform-specific control. */
	handled_fields?: string[];
}
export interface PlatformFormat {
	id: string;
	label: [string, string];
	entry_url?: string;
	preview?: Partial<PlatformContract["preview"]>;
	browser?: Partial<PlatformContract["browser"]>;
	/** A kind of post whose editor sends differently, or cannot send at all (`null`). */
	unattended?: Unattended | null;
}
/**
 * The contract for one kind of post: the platform's, with the format's own
 * address, fields, media and instructions laid over it. Everything that reads
 * a contract reads this one, so a format is invisible past here. An unknown
 * format is `undefined` — a task naming a kind the platform does not take is
 * not quietly published as another.
 */
export function withFormat(p: PlatformContract, format?: string | null): PlatformContract | undefined {
	if (!p.formats?.length) return format ? undefined : p;
	const f = format ? p.formats.find((x) => x.id === format) : p.formats[0];
	if (!f) return undefined;
	return {
		...p,
		entry_url: f.entry_url ?? p.entry_url,
		preview: { ...p.preview, ...f.preview },
		browser: { ...p.browser, ...f.browser },
		unattended: (f.unattended === undefined ? p.unattended : f.unattended) ?? undefined,
		format: f.id,
	};
}
/**
 * The kind of post an article most naturally becomes: its video where the
 * platform takes one, the long form when the text is past what a short post
 * holds, and the platform's default otherwise.
 */
export function suggestFormat(
	p: PlatformContract,
	work: { length: number; video: boolean; images: number },
): string | undefined {
	const kinds = p.formats?.map((f) => withFormat(p, f.id)!) ?? [];
	if (!kinds.length) return undefined;
	/* A kind the work cannot satisfy is not a suggestion. Douyin and Bilibili
	 * publish a video by default and an article besides it, so an article with no
	 * video was being offered the video flow and refused for wanting one — which
	 * reads as the platform being unavailable rather than as the wrong kind having
	 * been picked. Falling through on length did not save it either, because the
	 * length that would have to be exceeded is the video description's, and that
	 * has no limit to exceed. */
	const holds = (k: PlatformContract) => {
		const rule = k.preview.media;
		if (!rule?.required) return true;
		return rule.accept?.includes("video") && !rule.accept.includes("image")
			? work.video
			: work.images > 0 || work.video;
	};
	// Never empty: a platform whose every kind wants what this has not still has
	// to name one, and the caller shows why it does not fit.
	const open = kinds.filter(holds);
	const from = open.length ? open : kinds;
	const pick = (layout: string) => from.find((k) => k.preview.layout === layout)?.format;
	if (work.video) {
		const video = pick("video");
		if (video) return video;
	}
	const short = from[0].preview.fields.find((f) => f.key === "body")?.max_length;
	if (short && work.length > short) {
		const long = pick("article");
		if (long) return long;
	}
	return from[0].format;
}
export interface PublishPayload {
	version: 1;
	platform: string;
	/** One of the platform's `formats`; absent for a platform with one kind, and for the default. */
	format?: string;
	fields: Record<string, string>;
	media: { url: string; alt: string }[];
	source: { page_id: string; revision: number; canonical: string };
	target_url: string;
}
export interface BrowserTask {
	id: string;
	entity_id: string;
	payload: PublishPayload;
	state: string;
	published_url?: string | null;
	updated_at?: string;
	scheduled_at?: string | null;
	schedule_device?: string | null;
	schedule_account?: string | null;
	schedule_state?: string;
	schedule_error?: string | null;
}
export const platforms = registry as PlatformContract[];
export function platformById(id: string) {
	return platforms.find((p) => p.id === id);
}
/** The registry contract a payload is for, with its format resolved; `undefined` for an unknown platform or format. */
export function contractFor(payload: Pick<PublishPayload, "platform" | "format">) {
	const p = platformById(payload.platform);
	return p && withFormat(p, payload.format);
}
/**
 * The format as stored — "" for a platform with one kind — so a task that
 * left the default implicit and a calendar row that wrote it out compare equal.
 */
export function formatKey(p: PlatformContract, format?: string | null): string {
	return withFormat(p, format)?.format ?? "";
}
/** "小红书 · 视频": the platform, and the kind of post when it has several. */
export function kindName(p: PlatformContract, format: string | null | undefined, locale: "en" | "zh") {
	const f = p.formats?.find((x) => x.id === formatKey(p, format));
	return f ? `${p.name} · ${f.label[locale === "zh" ? 1 : 0]}` : p.name;
}
/** A platform's kinds of post, for a picker; empty when it has one. */
export function formatsOf(p: PlatformContract): PlatformFormat[] {
	return p.formats ?? [];
}
/** The part of a brand's own platform (Settings → Publishing) a contract is drawn from. */
export interface CustomPlatformConfig {
	id: string;
	name: string;
	url: string;
	format: string;
	images: string;
	video: string;
	max_length: number | null;
}
/**
 * A brand's own platform as a guided browser contract.
 *
 * The server draws the same one (`platform_publishing::custom_contract`) and
 * pins its hash on a variant; the two have to agree on shape, not on bytes.
 */
export function customContract(config: CustomPlatformConfig): PlatformContract {
	let host = "";
	try {
		host = new URL(config.url).hostname.replace(/^www\./, "");
	} catch {}
	const layout = config.format === "social" || config.format === "video" ? config.format : "article";
	const accept = [config.images !== "no" && "image/*", config.video !== "no" && "video/*"].filter(Boolean);
	return {
		id: config.id,
		name: config.name,
		entry_url: config.url,
		hosts: [host],
		methods: ["browser"],
		dynamic_host: false,
		preview: {
			layout,
			fields: [
				{
					key: "title",
					label: ["Title", "标题"],
					kind: "text",
					required: layout !== "social",
					max_length: null,
					transfer: "guided",
					selectors: [],
				},
				{
					key: "body",
					label: ["Body", "正文"],
					kind: "textarea",
					required: true,
					max_length: config.max_length,
					transfer: "guided",
					selectors: [],
					format: "plain",
				},
			],
			media: accept.length ? { accept: accept.join(","), required: false, multiple: true, transfer: "guided" } : null,
		},
		browser: {
			verification: "needs_live_verification",
			fill: "assisted",
			final_action: "user",
			instructions: [
				"Open the platform, paste the prepared text into its editor, add any media, and publish there yourself.",
				"打开该平台，把准备好的内容粘贴到编辑器中，补充图片或视频后，由你在平台上完成发布。",
			],
		},
		upstream_ref: "custom",
	};
}
export function safeWebUrl(value: string) {
	try {
		const u = new URL(value);
		return u.protocol === "https:" && !u.username && !u.password;
	} catch {
		return false;
	}
}
/* Plain HTTP is accepted for pictures and media: a brand file saved into a
 * task is served from the API's own origin, which is HTTP in development. */
export function assetUrl(value: string) {
	try {
		const u = new URL(value);
		return ["http:", "https:"].includes(u.protocol) && !u.username && !u.password;
	} catch {
		return false;
	}
}
export function allowedTarget(p: PlatformContract, value: string) {
	if (!safeWebUrl(value)) return false;
	const u = new URL(value);
	if (p.dynamic_host) return !["localhost", "127.0.0.1", "[::1]"].includes(u.hostname);
	return p.hosts.some((h) => u.hostname === h || u.hostname.endsWith(`.${h}`));
}
export function validPublishingSource(source: PublishPayload["source"]): boolean {
	if (!source || !Number.isInteger(source.revision) || source.revision < 1) return false;
	if (!source.page_id) return false;
	try {
		const url = new URL(source.canonical);
		return ["http:", "https:"].includes(url.protocol) && !!url.hostname && !url.username && !url.password;
	} catch {
		return false;
	}
}

/** `contract` is for a platform the registry does not hold, which a brand's own is. */
export function validatePayload(payload: PublishPayload, contract?: PlatformContract): string[] {
	const base = contract?.id === payload.platform ? contract : platformById(payload.platform);
	if (!base || payload.version !== 1) return ["platform"];
	const p = withFormat(base, payload.format);
	if (!p) return ["format"];
	const issues: string[] = [];
	if (!validPublishingSource(payload.source)) issues.push("source");
	if (!allowedTarget(p, payload.target_url)) issues.push("target_url");
	for (const f of p.preview.fields) {
		const v = payload.fields[f.key] ?? "";
		if (
			(fieldRequired(f, payload.fields) && !v.trim()) ||
			(f.max_length !== null && fieldLength(f, v) > f.max_length) ||
			(v && f.min_length !== undefined && fieldLength(f, v) < f.min_length) ||
			(v && f.kind === "url" && !safeWebUrl(v)) ||
			(v && f.kind === "image" && !v.startsWith("ondit-file:") && !assetUrl(v)) ||
			(v && f.options && !f.options.includes(v))
		)
			issues.push(f.key);
	}
	if (p.id === "youtube" && youtubeTagsLength(payload.fields.tags ?? "") > 500) issues.push("tags");
	if (payload.media.some((m) => !assetUrl(m.url)) || mediaBroken(p.preview.media, payload.media)) issues.push("media");
	return issues;
}

/** Only an extension says a URL is a video; an address that does not say is never refused for it. */
export function isVideoUrl(value: string) {
	try {
		return /\.(mp4|mov|webm)$/.test(new URL(value).pathname.toLowerCase());
	} catch {
		return false;
	}
}

function mediaBroken(rule: MediaRule | null, media: PublishPayload["media"]) {
	if (!rule) return false;
	const n = media.length,
		video = media.some((m) => isVideoUrl(m.url));
	return (
		(rule.multiple === false && n > 1) ||
		(rule.max_count !== undefined && n > rule.max_count) ||
		// Media can be uploaded in the platform instead, which is why `required` is not enforced
		// either; a minimum only applies to what the handoff does carry.
		(rule.min_count !== undefined && n > 0 && n < rule.min_count) ||
		(!!rule.video_alone && video && n > 1) ||
		(!!rule.no_video && video)
	);
}

/** YouTube caps all tags together at 500 characters, and quotes a tag with a space in it. */
export function youtubeTagsLength(tags: string) {
	return tags
		.split(",")
		.map((t) => t.trim())
		.filter(Boolean)
		.reduce((sum, t) => sum + Array.from(t).length + (/\s/.test(t) ? 2 : 0), 0);
}

export function fieldRequired(field: Field, values: Record<string, string>) {
	return field.required || !!(field.required_when && values[field.required_when.field] === field.required_when.value);
}

export function allowedPublication(p: PlatformContract, value: string, target: string) {
	if (!safeWebUrl(value)) return false;
	const u = new URL(value);
	if (p.publication_custom_domain) return true;
	if (p.dynamic_host) return u.origin === new URL(target).origin;
	return [...p.hosts, ...(p.publication_hosts ?? [])].some((h) => u.hostname === h || u.hostname.endsWith(`.${h}`));
}

export function fieldLength(field: Field, value: string) {
	return weightedLength(value, field.length_metric === "twitter");
}

/** X counts URLs and CJK by its own weights; everywhere else a character is a code point. */
export function weightedLength(value: string, twitter: boolean) {
	return twitter ? twitterText.parseTweet(value).weightedLength : Array.from(value).length;
}

/**
 * Every kind of post an unattended browser can drive to the end, as
 * `platform` or `platform:format`.
 *
 * Derived from the contracts rather than listed here. A platform joins by
 * being observed and written down, which is the only way it should: the list
 * this replaced was three ids in a source file, and adding a fourth meant
 * editing three files that had no reason to know about each other. LinkedIn
 * is absent for a reason worth keeping — its rewritten feed hashes every
 * class and its Post control carries only a per-render key, so there is
 * nothing stable to click.
 */
export function unattendedKinds(): string[] {
	const out: string[] = [];
	for (const p of platforms) {
		for (const format of p.formats?.map((f) => f.id) ?? [undefined]) {
			const kind = withFormat(p, format);
			if (kind?.unattended) out.push(format ? `${p.id}:${format}` : p.id);
		}
	}
	return out;
}

/** Hard platform constraints that cannot be repaired in the editor at run time. */
export function schedulingIssues(payload: PublishPayload): string[] {
	const base = platformById(payload.platform);
	const p = base && withFormat(base, payload.format);
	if (!p?.unattended) return ["platform"];
	/* A selectorless choice is no longer a scheduling refusal. The browser first
	 * uses the deterministic adapter and then gives the bounded recovery agent
	 * the visible controls. The payload is still validated before this point, so
	 * recovery can choose only the value the person already approved. */
	const issues: string[] = [];
	if (p.id === "youtube" && (payload.media.length !== 1 || payload.fields.visibility !== "public"))
		issues.push("media_or_visibility");
	if (p.id === "devto" && payload.media.length) issues.push("media");
	if (p.id === "devto" && (payload.fields.tags ?? "").split(/[,\s]+/).filter(Boolean).length > 4) issues.push("tags");
	if (p.id === "x" && payload.media.length > 4) issues.push("media");
	return issues;
}
